import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";
import { useListing } from "@/hooks/useListing";
import { useAuth } from "@/hooks/useAuth";
import { BiddingCards } from "./BiddingCards";
import { Pagination } from "@/components/ui/pagination";
import { usePersistedPageSize } from "@/hooks/usePersistedPageSize";

export const MyBiddingsView = () => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = usePersistedPageSize(
    "pageSize:myBiddings",
    10,
    [10, 15, 20, 25],
  );

  // Fetch data using hooks
  const { user } = useAuth();
  const { getMyBiddingsParticipation } = useListing();

  // Get my biddings participation (auctions where user has bid)
  const {
    data: myBiddingsData,
    isLoading: isLoadingBiddings,
    error: biddingsError,
  } = getMyBiddingsParticipation(
    {
      page: currentPage,
      limit: itemsPerPage,
    },
    { enabled: !!user }
  );

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  if (isLoadingBiddings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{o.loading.biddings}</div>
      </div>
    );
  }

  if (biddingsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-500">
          {o.error.fetchBiddings}
        </div>
      </div>
    );
  }

  const biddingsListings = myBiddingsData?.listings || [];
  const biddingsTotal = myBiddingsData?.totalCount || 0;
  const biddingsTotalPages = Math.ceil(biddingsTotal / itemsPerPage);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {(o.showingBiddings || "Visar {count} av {total} budgivningar")
            .replace("{count}", String(biddingsListings.length))
            .replace("{total}", String(biddingsTotal))}
        </div>
      </div>

      <BiddingCards listings={biddingsListings} />

      {/* Pagination */}
      {biddingsListings.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={biddingsTotalPages}
          totalItems={biddingsTotal}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          itemsPerPageOptions={[10, 15, 20, 25]}
        />
      )}
    </div>
  );
};
